import Phaser from 'phaser';
import { SCENE_KEYS, GAME_CONFIG } from '@/config/constants';
import { transitionTo, fadeIn } from '@/utils/UIAnimations';
import { createPillButton } from '@/ui/PillButton';
import { drawSelectionHalo } from '@/ui/SelectionHalo';
import { AudioManager } from '@/utils/AudioManager';
import { FRAME_KEYS } from '@/utils/AnimationManager';
import { COLORS, COLORS_HEX, RADIUS, TEXT_PRESET } from '@/ui/theme';
import { GamepadNav } from '@/utils/GamepadNav';

/**
 * Tutorial Scene — AAA interactive walkthrough using actual game assets
 */

interface TutorialPage {
  title: string;
  body: string;
  // Asset key to display as the visual (game sprite/image)
  assetKey?: string;
  assetScale?: number;
  accent?: string;
}

export class TutorialScene extends Phaser.Scene {
  private currentPage = 0;
  private pages: TutorialPage[] = [];
  private cardContainer?: Phaser.GameObjects.Container;
  private titleText?: Phaser.GameObjects.Text;
  private bodyText?: Phaser.GameObjects.Text;
  private assetImage?: Phaser.GameObjects.Image | Phaser.GameObjects.Sprite;
  private pageIndicator?: Phaser.GameObjects.Text;
  private pageDots: Phaser.GameObjects.Arc[] = [];
  private nav?: GamepadNav;

  constructor() {
    super({ key: SCENE_KEYS.TUTORIAL });
  }

  update(): void {
    this.nav?.update();
  }

  create(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const cx = WIDTH / 2;
    const cy = HEIGHT / 2;
    (this as any)._transitioning = false;
    fadeIn(this, 400);

    // Background — shared crystal-energy briefing shell
    const videoKey = 'bg_video_world1_video';
    if (this.cache.video.has(videoKey)) {
      const videoBg = this.add.video(cx, cy, videoKey);
      videoBg.setScale(1).play(true).setVolume(0).setDepth(0).setAlpha(0.7);
    } else {
      const bg = this.add.graphics();
      bg.fillGradientStyle(COLORS.midnightDepth, COLORS.midnightDepth, COLORS.deepOcean, COLORS.deepOcean, 1);
      bg.fillRect(0, 0, WIDTH, HEIGHT);
    }

    const overlay = this.add.graphics().setDepth(1);
    overlay.fillGradientStyle(COLORS.midnightDepth, COLORS.midnightDepth, COLORS.abyssBlue, COLORS.abyssBlue, 0.84, 0.84, 0.94, 0.94);
    overlay.fillRect(0, 0, WIDTH, HEIGHT);

    const glows = this.add.graphics().setDepth(2);
    glows.fillStyle(COLORS.aqua, 0.1);
    glows.fillCircle(cx, cy - 220, 260);
    glows.fillStyle(COLORS.sapphire, 0.12);
    glows.fillCircle(cx - 420, cy + 120, 220);
    glows.fillCircle(cx + 420, cy + 80, 220);

    for (let i = 0; i < 4; i++) {
      const rayX = WIDTH * 0.18 + i * WIDTH * 0.21;
      const ray = this.add.graphics().setDepth(2).setAlpha(0);
      ray.fillStyle(COLORS.pearlWhite, 1);
      ray.fillTriangle(rayX - 44, -20, rayX + 44, -20, rayX + 120 + i * 24, HEIGHT * 0.82);
      this.tweens.add({
        targets: ray,
        alpha: { from: 0, to: 0.035 + i * 0.01 },
        duration: 1800,
        delay: 220 + i * 160,
        ease: 'Power2',
      });
      this.tweens.add({
        targets: ray,
        x: { from: -18, to: 18 },
        duration: 5600 + i * 500,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    const vignette = this.add.graphics().setDepth(3);
    vignette.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.8, 0.8, 0, 0).fillRect(0, 0, WIDTH, 170);
    vignette.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0, 0, 0.85, 0.85).fillRect(0, HEIGHT - 160, WIDTH, 160);

    this.add.text(cx, 86, 'TACTICAL BRIEFING', {
      ...TEXT_PRESET.label,
      fontSize: '18px',
      color: COLORS_HEX.aquaLight,
      letterSpacing: 6,
    } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5).setDepth(20);

    this.add.text(cx, 128, 'Learn the arcade flow, then dive straight into Stage 1.', {
      ...TEXT_PRESET.body,
      fontSize: '24px',
      color: COLORS_HEX.textSecondary,
    } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5).setDepth(20);

    // Define pages using actual game assets
    this.pages = [
      {
        title: 'WELCOME TO THE RUN',
        body: 'Each voyage starts fresh at Stage 1.\nPop every threat, beat the clock,\nand keep the seas clear long enough to reach the Hydra.',
        assetKey: `${FRAME_KEYS.PLAYER.IDLE}_1`,
        assetScale: 2.0,
        accent: 'FRESH START',
      },
      {
        title: 'MOVEMENT',
        body: 'Move with the arrow keys.\nDouble-tap a direction to dash through danger\nand reset your footing before the next split.',
        assetKey: `${FRAME_KEYS.PLAYER.WALK}_1`,
        assetScale: 2.0,
        accent: 'STAY MOBILE',
      },
      {
        title: 'SHOOTING',
        body: 'Press SPACE or UP to fire.\nHold the shot to charge a heavier blast\nwhen the arena gets crowded.',
        assetKey: `${FRAME_KEYS.PLAYER.SHOOT}_1`,
        assetScale: 2.0,
        accent: 'OWN THE CENTER',
      },
      {
        title: 'BUBBLES',
        body: 'Large bubbles fracture into smaller threats.\nClear every remaining bubble to finish the stage,\nand watch the toxic variants carefully.',
        assetKey: 'bubble_regular',
        assetScale: 1.2,
        accent: 'CLEAR THE FIELD',
      },
      {
        title: 'COMBOS',
        body: 'Chain matching bubble sizes for multipliers.\nx2 becomes x4, then x8,\nand the run turns into Poseidon’s Wrath.',
        assetKey: 'star_v2_earned',
        assetScale: 0.6,
        accent: 'CHASE MOMENTUM',
      },
      {
        title: 'POWERUPS',
        body: 'Catch powerups on the drop.\nShield buys safety, Time Freeze buys breathing room,\nand Rapid Fire lets you reclaim control.',
        assetKey: 'powerup_shield',
        assetScale: 0.15,
        accent: 'TAKE THE EDGE',
      },
      {
        title: 'BOSS BATTLE',
        body: 'Stage 7 is the Pollution Hydra.\nSurvive the patterns, watch for weak windows,\nand punish the exposed core.',
        assetKey: 'boss_frame_1',
        assetScale: 0.35,
        accent: 'FINAL CHECK',
      },
      {
        title: 'DIVE IN',
        body: 'Clear fast, score hard, and push for the leaderboard.\nLose the run and the voyage resets,\nso every stage should feel earned.',
        assetKey: 'title_logo_v4',
        assetScale: 0.6,
        accent: 'READY',
      },
    ];

    // ─── BRIEFING CARD ────────────────────────────────────
    this.cardContainer = this.add.container(cx, cy);
    this.cardContainer.setDepth(10);

    const cardW = 1080;
    const cardH = 720;
    const cardR = RADIUS.xl;
    const cardBg = this.add.graphics();
    cardBg.fillStyle(COLORS.aqua, 0.08);
    cardBg.fillRoundedRect(-cardW / 2 - 12, -cardH / 2 - 10, cardW + 24, cardH + 22, cardR + 10);
    cardBg.fillStyle(COLORS.midnightDepth, 0.5);
    cardBg.fillRoundedRect(-cardW / 2, -cardH / 2 + 8, cardW, cardH, cardR);
    cardBg.fillGradientStyle(COLORS.tidewater, COLORS.tidewater, COLORS.abyssBlue, COLORS.abyssBlue, 0.95, 0.95, 0.92, 0.92);
    cardBg.fillRoundedRect(-cardW / 2, -cardH / 2, cardW, cardH, cardR);
    cardBg.fillStyle(COLORS.pearlWhite, 0.06);
    cardBg.fillRoundedRect(-cardW / 2 + 10, -cardH / 2 + 10, cardW - 20, 132, cardR - 10);
    cardBg.lineStyle(2, COLORS.aqua, 0.65);
    cardBg.strokeRoundedRect(-cardW / 2, -cardH / 2, cardW, cardH, cardR);
    cardBg.lineStyle(1, COLORS.pearlWhite, 0.22);
    cardBg.strokeRoundedRect(-cardW / 2 + 6, -cardH / 2 + 6, cardW - 12, cardH - 12, cardR - 6);
    cardBg.fillStyle(COLORS.aquaLight, 0.3);
    cardBg.fillRoundedRect(-cardW / 2 + 70, -cardH / 2 + 4, cardW - 140, 3, 2);
    this.cardContainer.add(cardBg);

    const assetWell = this.add.graphics();
    assetWell.fillStyle(COLORS.midnightDepth, 0.5);
    assetWell.fillRoundedRect(-280, -120, 560, 280, 30);
    assetWell.fillStyle(COLORS.aqua, 0.06);
    assetWell.fillRoundedRect(-272, -112, 544, 110, 24);
    assetWell.lineStyle(2, COLORS.sapphireLight, 0.4);
    assetWell.strokeRoundedRect(-280, -120, 560, 280, 30);
    assetWell.lineStyle(1, COLORS.pearlWhite, 0.16);
    assetWell.strokeRoundedRect(-274, -114, 548, 268, 24);
    this.cardContainer.add(assetWell);

    this.titleText = this.add.text(0, -278, '', {
      ...TEXT_PRESET.subtitle,
      fontSize: '64px',
      color: COLORS_HEX.goldLight,
      letterSpacing: 1,
    } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5);
    this.cardContainer.add(this.titleText);

    this.assetImage = this.add.image(0, 8, '__DEFAULT').setVisible(false);
    this.cardContainer.add(this.assetImage);

    this.bodyText = this.add.text(0, 220, '', {
      ...TEXT_PRESET.bodyBig,
      fontSize: '30px',
      color: COLORS_HEX.textSecondary,
      align: 'center',
      lineSpacing: 14,
      wordWrap: { width: 760, useAdvancedWrap: true },
    } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5);
    this.cardContainer.add(this.bodyText);

    this.pageDots = [];
    const dotSpacing = 32;
    const dotsStartX = -((this.pages.length - 1) * dotSpacing) / 2;
    for (let i = 0; i < this.pages.length; i++) {
      const dot = this.add.circle(dotsStartX + i * dotSpacing, 304, 8, COLORS.deepOcean);
      dot.setStrokeStyle(2, COLORS.aquaLight, 0.2);
      this.cardContainer.add(dot);
      this.pageDots.push(dot);
    }

    const prevBtn = createPillButton(this, {
      x: 180, y: cy,
      label: 'PREV',
      variant: 'secondary',
      size: 'small',
      width: 160, height: 56,
      onClick: () => this.changePage(-1),
    });
    prevBtn.setDepth(20);

    const nextBtn = createPillButton(this, {
      x: WIDTH - 180, y: cy,
      label: 'NEXT',
      variant: 'secondary',
      size: 'small',
      width: 160, height: 56,
      onClick: () => this.changePage(1),
    });
    nextBtn.setDepth(20);

    const backBtn = createPillButton(this, {
      x: cx, y: HEIGHT - 62,
      label: 'BACK TO MENU',
      variant: 'secondary',
      size: 'medium',
      width: 320, height: 68,
      onClick: () => transitionTo(this, SCENE_KEYS.MENU),
    });
    backBtn.setDepth(20);

    this.pageIndicator = this.add.text(cx, HEIGHT - 152, '', {
      ...TEXT_PRESET.caption,
      fontSize: '20px',
      color: COLORS_HEX.textTertiary,
      letterSpacing: 2,
    } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5).setDepth(20);

    this.add.text(cx, HEIGHT - 122, 'Use ARROWS to flip pages. Press ENTER to leave the briefing.', {
      ...TEXT_PRESET.caption,
      fontSize: '18px',
      color: COLORS_HEX.aquaLight,
    } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5).setDepth(20).setAlpha(0.76);

    // Keyboard nav — Left/Right flip pages, ENTER exits.
    this.input.keyboard?.addKey('LEFT').on('down', () => this.changePage(-1));
    this.input.keyboard?.addKey('RIGHT').on('down', () => this.changePage(1));
    this.input.keyboard?.addKey('ENTER').on('down', () => transitionTo(this, SCENE_KEYS.MENU));

    // Gamepad — D-pad / stick cycles focus through PREV / NEXT / BACK pills.
    // A and B both press the focused pill (so either face button acts as
    // "select" on whatever the halo is on). Start exits as a courtesy.
    const buttons = [prevBtn, nextBtn, backBtn];
    let focusIdx = -1;
    const halo = this.add.graphics().setDepth(21);
    const drawHalo = () => {
      if (focusIdx < 0) { halo.clear(); return; }
      const b = buttons[focusIdx];
      if (!b.active) { halo.clear(); return; }
      const w = ((b as any).pillWidth ?? 160) * b.scaleX;
      const h = ((b as any).pillHeight ?? 56) * b.scaleY;
      drawSelectionHalo(halo, {
        cx: b.x, cy: b.y,
        w, h,
        rx: ((b as any).pillCapRx ?? h / 2) * b.scaleX,
        ry: ((b as any).pillCapRy ?? h / 2) * b.scaleY,
      }, { gap: 12 });
    };
    this.events.on(Phaser.Scenes.Events.UPDATE, drawHalo);
    prevBtn.on('pointerover', () => { focusIdx = 0; drawHalo(); });
    nextBtn.on('pointerover', () => { focusIdx = 1; drawHalo(); });
    backBtn.on('pointerover', () => { focusIdx = 2; drawHalo(); });

    const step = (dir: number) => {
      focusIdx = focusIdx < 0
        ? (dir >= 0 ? 0 : buttons.length - 1)
        : Phaser.Math.Clamp(focusIdx + dir, 0, buttons.length - 1);
      drawHalo();
    };
    const confirm = () => { if (focusIdx >= 0) (buttons[focusIdx] as any).press?.(); };

    this.nav = new GamepadNav(this, {
      onLeft:  () => step(-1),
      onRight: () => step(1),
      onUp:    () => step(-1),
      onDown:  () => step(1),
      onConfirm: confirm,
      onPause:   () => transitionTo(this, SCENE_KEYS.MENU),
    });

    // Show first page
    this.showPage(0);
  }

  private changePage(dir: number): void {
    const newPage = this.currentPage + dir;
    if (newPage < 0 || newPage >= this.pages.length) return;
    // AAA: paper page-turn SFX on navigation
    const audio = AudioManager.getInstance();
    if (this.cache.audio.exists('sfx_page_turn')) {
      audio.playSFX(this, 'sfx_page_turn', { volume: 0.55 });
    } else {
      audio.playSFX(this, 'sfx_btn_click', { volume: 0.3 });
    }
    this.showPage(newPage);
  }

  private showPage(index: number): void {
    const previousPage = this.currentPage;
    const isInitial = !this.titleText?.text;
    this.currentPage = index;
    const page = this.pages[index];

    const applyPage = () => {
      this.titleText?.setText(page.title);
      this.bodyText?.setText(page.body);

      if (this.assetImage) {
        if (page.assetKey && this.textures.exists(page.assetKey)) {
          this.assetImage.setTexture(page.assetKey);
          this.assetImage.setScale(page.assetScale || 1);
          this.assetImage.setVisible(true);
        } else {
          this.assetImage.setVisible(false);
        }
      }

      this.pageDots.forEach((dot, i) => {
        dot.setFillStyle(i === index ? COLORS.aquaLight : COLORS.deepOcean);
        dot.setStrokeStyle(2, i === index ? COLORS.pearlWhite : COLORS.aquaLight, i === index ? 0.65 : 0.12);
        dot.setScale(i === index ? 1.35 : 1);
      });

      this.pageIndicator?.setText(`${page.accent ?? 'BRIEFING'} • ${index + 1} / ${this.pages.length}`);
    };

    if (!this.cardContainer) return;

    if (isInitial) {
      applyPage();
      this.cardContainer.setAlpha(0).setScale(0.96);
      this.tweens.add({
        targets: this.cardContainer,
        alpha: 1,
        scale: 1,
        duration: 280,
        ease: 'Back.easeOut',
      });
      return;
    }

    const dir = index >= previousPage ? 1 : -1;
    this.tweens.add({
      targets: this.cardContainer,
      alpha: 0,
      x: GAME_CONFIG.WIDTH / 2 + dir * 46,
      duration: 120,
      ease: 'Power2.easeIn',
      onComplete: () => {
        applyPage();
        this.cardContainer!.x = GAME_CONFIG.WIDTH / 2 - dir * 46;
        this.tweens.add({
          targets: this.cardContainer,
          alpha: 1,
          x: GAME_CONFIG.WIDTH / 2,
          duration: 220,
          ease: 'Power2.easeOut',
        });
      },
    });
  }
}
